// The record screen: an active sitting. Walks a question queue, uploads each
// answer, and shows a live "This session" log with audio playback + re-record.
//
// This module self-initializes on import and lives for the whole page: the
// session, queue, and its Firestore subscription persist while the user browses
// their past sessions and comes back, so an in-progress sitting is never lost.

import {
  addDoc,
  collection,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
  doc as fsDoc,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { db, storage, ensureSignedIn } from "./firebase";
import { Recorder } from "./recorder";
import { QUESTIONS, type Question } from "./questions";
import { latestTakes } from "./session-data";

type UiState = "idle" | "recording" | "uploading" | "done" | "error";

// ---- DOM ----
const recordBtn = document.getElementById("recordBtn") as HTMLButtonElement;
const recordLabel = document.getElementById("recordLabel") as HTMLElement;
const timerEl = document.getElementById("timer") as HTMLElement;
const recIndicator = document.getElementById("recIndicator") as HTMLElement;
const statusPill = document.getElementById("statusPill") as HTMLElement;
const statusSection = document.querySelector(".status") as HTMLElement;
const messageEl = document.getElementById("message") as HTMLElement;
const questionEl = document.getElementById("question") as HTMLElement;
const skipBtn = document.getElementById("skipBtn") as HTMLButtonElement;
const sessionLogLabel = document.getElementById("sessionLogLabel") as HTMLElement;
const sessionEmpty = document.getElementById("sessionEmpty") as HTMLElement;
const sessionLogList = document.getElementById("sessionLogList") as HTMLElement;

// ---- Session + question cursor ----
// A stable per-page session id groups this sitting's recordings and its
// sessions/{id} metadata doc.
const sessionId = crypto.randomUUID();

const questionById = new Map<string, Question>(QUESTIONS.map((q) => [q.id, q]));
// The session's starting order (warm → tender). Persisted on the session doc.
const questionOrder = QUESTIONS.map((q) => q.id);
// Working queue of question ids still to ask; queue[0] is the current question.
// Answering shifts it off; skipping moves it to the back.
let queue: string[] = [...questionOrder];
// How many questions have been answered — the ordinal we stamp on each
// recording so the session log can order itself without a composite index.
let answeredCount = 0;
// The order stamped on each question the first time it's answered, reused for
// re-records so a redone answer keeps its place in the session list.
const answeredOrder = new Map<string, number>();
// When set, we're re-recording an already-answered question; this overrides the
// normal cursor (queue[0]) until the new take lands.
let reRecordingId: string | null = null;

let recorder: Recorder | null = null;
let timerHandle: number | null = null;

const STATUS_LABELS: Record<UiState, string> = {
  idle: "Ready",
  recording: "Recording",
  uploading: "Saving",
  done: "Done",
  error: "Error",
};

function currentQuestion(): Question | null {
  const id = reRecordingId ?? queue[0];
  return id ? (questionById.get(id) ?? null) : null;
}

function sessionComplete(): boolean {
  // Re-recording keeps us active even once the queue is empty.
  return queue.length === 0 && reRecordingId === null;
}

/** Busy = recording or still saving the take — no skipping mid-flight. */
function isBusy(): boolean {
  return recorder !== null || statusPill.dataset.state === "uploading";
}

function renderQuestion() {
  const q = currentQuestion();
  if (q) {
    questionEl.textContent = q.text;
    skipBtn.hidden = false;
    // While re-recording, the skip link becomes a way back out.
    skipBtn.textContent = reRecordingId
      ? "Keep my answer — go back"
      : "Not this one? Tap to skip — no explanation needed";
  } else {
    // Nothing left in the queue — a gentle, guilt-free close.
    questionEl.textContent =
      "That's everything for now. Thank you for sharing your stories.";
    skipBtn.hidden = true;
  }
}

function setState(state: UiState) {
  statusPill.dataset.state = state;
  statusPill.textContent = STATUS_LABELS[state];

  const isRecording = state === "recording";
  recordBtn.classList.toggle("recording", isRecording);
  recIndicator.hidden = !isRecording;
  timerEl.hidden = !isRecording;

  const busy = state === "uploading";
  // The status pill only earns its place while work is happening — idle, done,
  // and error are already clear from the button, the question, or the error
  // line, so keep the page quiet otherwise.
  statusSection.hidden = !busy;
  // Disable while working, and once every question has been answered.
  recordBtn.disabled = busy || sessionComplete();

  // While recording, the button becomes a teleprompter: it shows the
  // sentence-starter to read aloud (the stop-square icon stays as the cue to
  // tap and finish).
  if (isRecording)
    recordLabel.textContent = currentQuestion()?.starter ?? "Stop";
  else if (busy) recordLabel.textContent = "Working…";
  else if (sessionComplete()) recordLabel.textContent = "All done";
  else recordLabel.textContent = "Record";

  // The error line clears itself the moment we move on from the error state.
  if (state !== "error") messageEl.hidden = true;
}

// Developer diagnostics only — never shown on the page.
function debug(msg: string) {
  console.log("[mayse]", msg);
}

// Transcripts live in the "This session" list; the only thing the main column
// needs to surface directly is an error, in its own quiet line.
function showError(msg: string) {
  setState("error");
  messageEl.textContent = msg;
  messageEl.hidden = false;
}

function startTimer() {
  const startedAt = Date.now();
  const tick = () => {
    const secs = Math.floor((Date.now() - startedAt) / 1000);
    const mm = String(Math.floor(secs / 60)).padStart(2, "0");
    const ss = String(secs % 60).padStart(2, "0");
    timerEl.textContent = `${mm}:${ss}`;
  };
  tick();
  timerHandle = window.setInterval(tick, 250);
}

function stopTimer() {
  if (timerHandle !== null) {
    clearInterval(timerHandle);
    timerHandle = null;
  }
}

async function startRecording() {
  if (sessionComplete()) return;

  recorder = new Recorder();
  try {
    await recorder.start();
  } catch (err) {
    showError(`Could not start recording: ${(err as Error).message}`);
    recorder = null;
    return;
  }

  if (!recorder.wakeLock.supported) {
    debug("Wake Lock API not supported — screen may sleep.");
  } else {
    debug(`Recording • ${recorder.mimeType || "browser default"}`);
  }
  setState("recording");
  startTimer();
}

async function stopRecording() {
  if (!recorder) return;
  stopTimer();
  setState("uploading");

  let result;
  try {
    result = await recorder.stop();
  } catch (err) {
    showError(`Recording failed: ${(err as Error).message}`);
    recorder = null;
    return;
  }
  recorder = null;

  try {
    await uploadAndTrack(result);
  } catch (err) {
    showError(`Upload failed: ${(err as Error).message}`);
  }
}

// The sessions/{id} metadata doc is created lazily, on the first answer, rather
// than on page load — so merely opening the app (or browsing past sessions)
// never leaves an empty sitting in the session list.
let sessionStarted = false;
async function ensureSessionDoc() {
  if (sessionStarted) return;
  sessionStarted = true;
  try {
    const uid = await ensureSignedIn();
    await setDoc(fsDoc(db, "sessions", sessionId), {
      createdBy: uid,
      startedAt: serverTimestamp(),
      status: "active",
      questionOrder,
    });
  } catch (err) {
    sessionStarted = false; // let the next answer try again
    debug(`Session doc not created: ${(err as Error).message}`);
  }
}

async function uploadAndTrack(result: {
  blob: Blob;
  mimeType: string;
  ext: string;
  durationSec: number;
}) {
  const uid = await ensureSignedIn();

  const question = currentQuestion();
  if (!question) return; // session already complete

  // The session doc must exist before its recordings so the list has something
  // to group them under (and so transcript edits can look up its owner).
  await ensureSessionDoc();

  // First answer to a question claims the next ordinal; re-records reuse it so
  // the redone answer keeps its place in the session list.
  let order = answeredOrder.get(question.id);
  if (order === undefined) {
    order = answeredCount++;
    answeredOrder.set(question.id, order);
  }

  const timestamp = Date.now();
  // uid in the path lets Storage rules enforce per-owner access (they can't read
  // Firestore). The full path is stored on the doc, so playback is unaffected.
  const storagePath = `recordings/${uid}/${sessionId}/${timestamp}.${result.ext}`;

  debug(`Uploading ${(result.blob.size / 1024).toFixed(0)} KB → ${storagePath}`);
  await uploadBytes(ref(storage, storagePath), result.blob, {
    contentType: result.mimeType,
  });

  // Create the Firestore doc the Cloud Function will update with the transcript.
  // The flat `recordings` collection is unchanged for the function; we just add
  // the session/question fields alongside it.
  const docRef = await addDoc(collection(db, "recordings"), {
    createdBy: uid, // owner stamp — rules scope reads to this (M3 privacy)
    sessionId,
    questionId: question.id,
    question: question.text,
    questionOrder: order,
    skipped: false,
    storagePath,
    mimeType: result.mimeType,
    durationSec: result.durationSec,
    createdAt: serverTimestamp(),
    status: "uploaded",
  });

  // Transcription runs in the background: the session log below already
  // subscribes to this session's recordings, so it shows the new answer as
  // "Transcribing…" and fills in the text (or a failure note) when Whisper
  // lands. Don't make the storyteller wait — move straight to the next question.
  debug(`Answer saved (${docRef.id.slice(0, 6)}…); transcribing in background`);
  finishAnswer();
  setState(sessionComplete() ? "done" : "idle");
}

/** A take landed: return from a re-record, or advance the cursor. */
function finishAnswer() {
  if (reRecordingId !== null) {
    reRecordingId = null; // done re-recording; back to the cursor
  } else {
    queue.shift();
  }
  renderQuestion();
}

/** Re-record an already-answered question; its new take replaces the old. */
function beginReRecord(questionId: string) {
  if (isBusy() || !questionById.has(questionId)) return;
  reRecordingId = questionId;
  renderQuestion();
  setState("idle");
  recordBtn.scrollIntoView({ behavior: "smooth", block: "center" });
}

/** Back out of a re-record without changing the saved answer. */
function cancelReRecord() {
  if (isBusy()) return;
  reRecordingId = null;
  renderQuestion();
  setState(sessionComplete() ? "done" : "idle");
}

/** Skip: no explanation needed — re-queue to the back and move on. */
function skipQuestion() {
  if (isBusy() || sessionComplete()) return;
  const id = queue.shift();
  if (!id) return;
  queue.push(id);
  renderQuestion();
  setState("idle");
}

// ---- Session progress view ----
// Live list of everything answered this session, with audio to play back each
// answer and a "Record again" action. Query by owner + session (both equality,
// no orderBy) so no composite index is needed. The `createdBy` filter matches
// the M3 privacy rule, so an empty session listens cleanly instead of erroring.
//
// Cards are keyed by questionId and reused across snapshots, so a playing
// <audio> isn't disturbed when some other card's status changes.
interface LogCard {
  li: HTMLLIElement;
  statusEl: HTMLElement;
  audio: HTMLAudioElement;
  docId: string | null;
}
const logCards = new Map<string, LogCard>();

function subscribeSessionLog(uid: string) {
  const q = query(
    collection(db, "recordings"),
    where("createdBy", "==", uid),
    where("sessionId", "==", sessionId),
  );
  onSnapshot(
    q,
    (snap) => {
      const takes = latestTakes(snap.docs);

      const hasAnswers = takes.length > 0;
      sessionLogLabel.hidden = !hasAnswers;
      sessionEmpty.hidden = hasAnswers;

      for (const { id, data } of takes) {
        const qid = (data.questionId as string) ?? id;
        let card = logCards.get(qid);
        if (!card) {
          card = createLogCard(qid);
          logCards.set(qid, card);
          // Newest answer on top: each freshly answered question has a higher
          // order than the ones before it, so prepending the new card puts the
          // most recent first. A re-record reuses its existing card, so it stays
          // put (and a playing <audio> is never disturbed).
          sessionLogList.prepend(card.li);
        }
        updateLogCard(card, id, data);
      }
    },
    (err) => debug(`Session log error: ${err.message}`),
  );
}

function createLogCard(questionId: string): LogCard {
  const li = document.createElement("li");
  li.className = "session-log-item";

  const qEl = document.createElement("p");
  qEl.className = "session-log-q";
  qEl.textContent = questionById.get(questionId)?.text ?? "Question";

  const audio = document.createElement("audio");
  audio.className = "session-log-audio";
  audio.controls = true;
  audio.preload = "none";

  const statusEl = document.createElement("p");
  statusEl.className = "session-log-a";

  const again = document.createElement("button");
  again.type = "button";
  again.className = "session-log-again";
  again.textContent = "Record again";
  again.addEventListener("click", () => beginReRecord(questionId));

  li.append(qEl, audio, statusEl, again);
  return { li, statusEl, audio, docId: null };
}

function updateLogCard(
  card: LogCard,
  docId: string,
  data: Record<string, unknown>,
) {
  // Only reload the audio when the underlying take changes (e.g. a re-record),
  // so playback isn't interrupted by unrelated status updates.
  if (card.docId !== docId) {
    card.docId = docId;
    const storagePath = data.storagePath as string | undefined;
    if (storagePath) {
      getDownloadURL(ref(storage, storagePath))
        .then((url) => {
          card.audio.src = url;
        })
        .catch((e) => debug(`Audio unavailable: ${(e as Error).message}`));
    }
  }

  if (data.status === "transcribed") {
    card.statusEl.textContent =
      (data.transcript as string) || "(empty transcript)";
    card.statusEl.classList.remove("muted");
  } else if (data.status === "error") {
    card.statusEl.textContent = "Couldn't transcribe this one.";
    card.statusEl.classList.add("muted");
  } else {
    card.statusEl.textContent = "Transcribing…";
    card.statusEl.classList.add("muted");
  }
}

recordBtn.addEventListener("click", () => {
  if (recorder) void stopRecording();
  else void startRecording();
});

skipBtn.addEventListener("click", () => {
  if (reRecordingId !== null) cancelReRecord();
  else skipQuestion();
});

// Render the first question before auth resolves so the page isn't blank.
renderQuestion();

// Warm up anonymous auth so the first upload is fast. The session doc itself is
// created lazily on the first answer (see ensureSessionDoc). The session log
// subscribes only once we have a uid, since its query filters by owner (M3).
ensureSignedIn()
  .then((uid) => {
    debug(`Signed in (anon): ${uid.slice(0, 8)}…`);
    subscribeSessionLog(uid);
    setState("idle");
  })
  .catch((err) => {
    showError(`Auth failed: ${(err as Error).message}. Check Firebase config.`);
  });
